import { useCallback } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material"
import { useDialogFormState } from "../../hooks/use-dialog-form-state"

export interface DeleteDashboardDialogProps {
  open: boolean
  // The dashboard's current name, shown in the confirmation copy.
  dashboardName: string
  onClose: () => void
  // Resolves → dialog closes; rejects → dialog stays open with the inline error.
  onDelete: () => Promise<void>
}

export default function DeleteDashboardDialog({
  open,
  dashboardName,
  onClose,
  onDelete,
}: DeleteDashboardDialogProps) {
  const { isBusy: isDeleting, error, beginBusy, endBusy, guardedClose } = useDialogFormState()

  const handleClose = useCallback(() => {
    guardedClose(onClose)
  }, [guardedClose, onClose])

  const handleDelete = useCallback(async () => {
    if (!beginBusy()) return
    try {
      await onDelete()
      onClose()
    } catch {
      endBusy("Couldn't delete the dashboard. Please try again.")
    }
  }, [beginBusy, onDelete, onClose, endBusy])

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Delete dashboard</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {`Delete "${dashboardName}"? This dashboard's current workspace state — its open panels and arrangement — will be lost. The layout it was created from is kept, so you can create a new dashboard from it again.`}
        </DialogContentText>
        {error !== null && (
          <DialogContentText color="error" sx={{ mt: 2 }}>
            {error}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isDeleting}>Cancel</Button>
        <Button onClick={() => { void handleDelete() }} disabled={isDeleting} color="error" variant="contained">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}
